import { observer } from 'mobx-react';
import { Layout } from '@roc/feature-app-core';
import { DataGrid, AgGridColumnProps, SelectField } from '@roc/ui';
import { useEffect, useState } from 'react';
import { useStore } from '@roc/client-portal-shared/hooks';
import { Typography, Button, Box, TextField, FormControl, InputLabel, Select, MenuItem, Grid } from '@material-ui/core';
import { BusinessUnit, BusinessUnitArray } from '@roc/feature-utils';

const columns: AgGridColumnProps[] = [
  {
    field: 'rmAe',
    headerName: 'RM/AE',
    minWidth: 160,
    maxWidth: 250,
    cellRenderer: 'nameRenderer',
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'totalInboundCalls',
    headerName: 'Total Inbound Calls',
    minWidth: 160,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'totalMissedCalls',
    headerName: 'Total Missed Calls',
    minWidth: 160,
    maxWidth: 250,
    cellRenderer: 'statusCellRenderer',
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'totalOutboundCalls',
    headerName: 'Total Outbound Calls',
    minWidth: 160,
    maxWidth: 250,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'totalAnswers',
    headerName: 'Total Answers',
    minWidth: 160,
    maxWidth: 250,
    cellRenderer: 'contentRenderer',
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'totalVoicemails',
    headerName: 'Total Voicemails',
    minWidth: 160,
    maxWidth: 250,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'totalOutboundTexts',
    headerName: 'Total Outbound Texts',
    minWidth: 160,
    maxWidth: 250,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'totalEmailsSent',
    headerName: 'Total Emails Sent',
    minWidth: 160,
    maxWidth: 250,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'totalNewLeadsEntered',
    headerName: 'Total New Leads Entered',
    minWidth: 160,
    maxWidth: 250,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'totalLoansSubmitted',
    headerName: 'Total Loans Submitted',
    minWidth: 160,
    maxWidth: 250,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
  {
    field: 'totalLeadsCreditBackgroundPulled',
    headerName: 'Total Leads Credit Background Pulled',
    minWidth: 160,
    maxWidth: 250,
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: { suppressFilterButton: true },
  },
];

export const businessUnitOptions = [
  { label: 'All', value: null },
  { label: BusinessUnit.ROC, value: BusinessUnit.ROC },
  { label: BusinessUnit.CIVIC, value: BusinessUnit.CIVIC },
  { label: BusinessUnit.FACO, value: BusinessUnit.FACO },
]

export const OutreachOverallDashboard = observer(() => {
  const { salesManagementStore } = useStore();
  const gridStore = salesManagementStore.outreachOverallGridStore;

  const initialDateFrom = gridStore.gridData?.meta?.dropdownFilters?.dateFrom ?? '';
  const initialDateTo = gridStore.gridData?.meta?.dropdownFilters?.dateTo ?? '';

  const [dateFrom, setDateFrom] = useState<string>(initialDateFrom);
  const [dateTo, setDateTo] = useState<string>(initialDateTo);
  const [businessUnit, setBusinessUnit] = useState<string>(null);

  useEffect(() => {
    gridStore.fetchGridData();
  }, []);

  useEffect(() => {
    const meta = gridStore.gridData?.meta;
    const dfMeta = meta?.dropdownFilters?.dateFrom ?? '';
    const dtMeta = meta?.dropdownFilters?.dateTo ?? '';
    const df = dateFrom ?? '';
    const dt = dateTo ?? '';

    if ((df && df !== dfMeta) || (dt && dt !== dtMeta)) {
      salesManagementStore.setOutreachDateRange(df || undefined, dt || undefined, businessUnit || undefined);
    }
  }, [gridStore.gridData?.meta, dateFrom, dateTo, salesManagementStore, businessUnit]);

  const keepDateFilters = () => {
    salesManagementStore.setOutreachDateRange(dateFrom || undefined, dateTo || undefined, businessUnit || undefined);
  };

  const onSortChanged = (params: any) => {
    const sortModel = params.api.getSortModel();
    gridStore.setSortModel(sortModel);
  };

  const onFilterChanged = (params: any) => {
    const filterModel = params.api.getFilterModel();
    gridStore.setFilterModel(filterModel);
  };

  const applyDateFilter = () => {
    if (dateFrom && dateTo && dateFrom > dateTo) return;
    salesManagementStore.setOutreachDateRange(dateFrom || undefined, dateTo || undefined, businessUnit || undefined);
    gridStore.setPageNumber(1);
    gridStore.fetchGridData();
  };

  const clearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
    setBusinessUnit(null)
    salesManagementStore.setOutreachDateRange(undefined, undefined, undefined);
    gridStore.setPageNumber(1);
    gridStore.fetchGridData();
  };

  const setLastNDays = (n: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - (n - 1));
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    setDateFrom(fmt(from));
    setDateTo(fmt(to));
  };

  const disableApply = !!(dateFrom && dateTo && dateFrom > dateTo);

  return (
    <Layout>
      <Box display="flex" alignItems="center" style={{ gap: 12 }} p={1} pl={2} flexWrap="wrap">
        <Box>
          <SelectField
            value={businessUnit}
            options={businessUnitOptions}
            onChange={value => {
              setBusinessUnit(value);
            }}
            fullWidth
            variant={'outlined'}
            testId={'businessUnit'}
            style={{ height: '35px' }}
          />
        </Box>
        <TextField
          label="From"
          type="date"
          variant="outlined"
          size="small"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: dateTo || undefined }}
        />
        <TextField
          label="To"
          type="date"
          variant="outlined"
          size="small"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: dateFrom || undefined }}
        />

        <Button variant="contained" color="primary" onClick={applyDateFilter} disabled={disableApply}>
          Apply
        </Button>
        <Button variant="outlined" onClick={clearDateFilter}>
          Clear
        </Button>

        <Box ml={1} display="flex" style={{ gap: 8 }}>
          <Button size="small" onClick={() => setLastNDays(7)}>Last 7d</Button>
          <Button size="small" onClick={() => setLastNDays(30)}>Last 30d</Button>
        </Box>
      </Box>

      <DataGrid
        columns={columns}
        rows={gridStore.gridData.data?.rows ?? []}
        isLoading={gridStore.isLoading}
        showFilters={gridStore.showFilters}
        sortModel={gridStore.sortModel}
        filterModel={gridStore.filterModel}
        paginationData={gridStore.gridData.meta}
        setPageNumber={gridStore.setPageNumber}
        onSortChanged={onSortChanged}
        onFilterChanged={onFilterChanged}
      />
    </Layout>
  );
});
